// @catalog F.030
// ProductDetailHeader — Header sticky del drawer.
// Breadcrumbs · imagen 64x64 · título h1 · SKU pill + categoría ·
// chips ABC·XYZ + demanda + Odoo 16/18 + toggle Compras ON/OFF visual.
//
// Toggle Compras ON/OFF es VISUAL-ONLY en esta versión: el backend no
// expone PATCH para `activeForPurchase` y el AGENT_PROMPT autoriza dejarlo
// como TODO (ver `onToggleActive`).

import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import type { ProductMapping } from "../productDetailTypes";
import {
  ABC_TONE,
  DEMAND_LABELS,
  DEMAND_TONE,
  TONE_BG_VAR,
  TONE_COLOR_VAR,
} from "./helpers";

interface ProductDetailHeaderProps {
  mapping: ProductMapping;
  productImage: string | null;
  activeForPurchase: boolean;
  /** Toggle visual-only; el handler solo actualiza state local del drawer. */
  onToggleActive: () => void;
  onClose: () => void;
}

function MiniChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: keyof typeof TONE_COLOR_VAR;
}) {
  const isNeutral = tone === "neutral";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 0.875,
        py: 0.25,
        borderRadius: 0.5,
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: "nowrap",
        bgcolor: isNeutral ? "var(--bg-2)" : TONE_BG_VAR[tone],
        color: isNeutral ? "var(--ink-2)" : TONE_COLOR_VAR[tone],
        border: isNeutral ? "1px solid var(--border)" : "1px solid transparent",
      }}
    >
      {label}
    </Box>
  );
}

export function ProductDetailHeader({
  mapping,
  productImage,
  activeForPurchase,
  onToggleActive,
  onClose,
}: ProductDetailHeaderProps) {
  const odooBadges = [];
  if (mapping.isActive16 && mapping.isActive18) odooBadges.push("Odoo 16/18");
  else if (mapping.isActive18) odooBadges.push("Odoo 18");
  else if (mapping.isActive16) odooBadges.push("Odoo 16");

  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        borderBottom: "1px solid var(--border)",
        px: 2.5,
        pt: 1.75,
        flexShrink: 0,
      }}
    >
      {/* Breadcrumbs row */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          mb: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        <span>Compras</span>
        <ChevronRightIcon sx={{ fontSize: 12 }} />
        <span>Forecast</span>
        <ChevronRightIcon sx={{ fontSize: 12 }} />
        <Box
          component="strong"
          sx={{
            color: "var(--ink-1)",
            fontWeight: 500,
          }}
        >
          Detalle del producto
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Cerrar"
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            color: "var(--ink-3)",
            "&:hover": {
              bgcolor: "var(--bg-2)",
              color: "var(--ink-1)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {/* Hero — imagen + identidad */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "112px 1fr",
          gap: 2,
          pb: 1.75,
        }}
      >
        <Box
          sx={{
            width: 112,
            height: 112,
            borderRadius: 1.25,
            bgcolor: "var(--bg-2)",
            border: "1px solid var(--border)",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {productImage ? (
            <img
              src={productImage}
              alt={mapping.unifiedName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Inventory2OutlinedIcon
              sx={{ fontSize: 48, color: "var(--ink-4)" }}
            />
          )}
        </Box>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: "var(--ink-1)",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {mapping.unifiedName}
          </Typography>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            flexWrap="wrap"
            sx={{ fontSize: 12.5, color: "var(--ink-3)" }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: "var(--font-mono)",
                fontSize: 11.5,
                bgcolor: "var(--bg-2)",
                color: "var(--ink-2)",
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {mapping.unifiedCode || "—"}
            </Box>
            <Box component="span" sx={{ color: "var(--ink-4)" }}>
              ·
            </Box>
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: 12.5,
                color: "var(--ink-3)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {mapping.unifiedCategory || "Sin categoría"}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ mt: 0.25 }}
          >
            {mapping.abcXyzSegment && (
              <MiniChip
                label={mapping.abcXyzSegment}
                tone={ABC_TONE[mapping.abcClassification ?? ""] ?? "neutral"}
              />
            )}
            {mapping.demandClassification && (
              <MiniChip
                label={
                  DEMAND_LABELS[mapping.demandClassification] ??
                  mapping.demandClassification
                }
                tone={DEMAND_TONE[mapping.demandClassification] ?? "neutral"}
              />
            )}
            {odooBadges.length > 0 && <MiniChip label={odooBadges[0]!} />}
            {/* Toggle Compras ON/OFF — persiste vía PATCH y saca/incluye el
                producto de la vista y el cálculo de compras. */}
            <Tooltip
              title="Activar/desactivar para compras"
              arrow
              placement="top"
            >
              <Box
                component="button"
                type="button"
                onClick={onToggleActive}
                aria-pressed={activeForPurchase}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                  px: 1.25,
                  borderRadius: 2,
                  fontSize: 12,
                  bgcolor: activeForPurchase ? "var(--pos-soft)" : "var(--bg-2)",
                  color: activeForPurchase ? "var(--pos)" : "var(--ink-2)",
                  border: "1px solid",
                  borderColor: activeForPurchase
                    ? "transparent"
                    : "var(--border)",
                  cursor: "pointer",
                  fontWeight: 500,
                  font: "inherit",
                  "&:focus-visible": {
                    outline: "2px solid var(--accent)",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 16,
                    height: 9,
                    borderRadius: 1,
                    bgcolor: activeForPurchase
                      ? "var(--pos)"
                      : "var(--border-strong)",
                    position: "relative",
                    transition: "background 0.15s",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: "7px",
                      height: "7px",
                      bgcolor: "#fff",
                      borderRadius: "50%",
                      top: "1px",
                      left: "1px",
                      transition: "transform 0.15s",
                      transform: activeForPurchase
                        ? "translateX(7px)"
                        : "translateX(0)",
                    },
                  }}
                />
                {activeForPurchase ? "Compras ON" : "Compras OFF"}
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default ProductDetailHeader;
